/**
 * orderFormParts.tsx — small pieces shared by the vendor order forms that sit
 * inside LabOrderModalCore (Axiom peptides, Partell prescriptions), so their
 * section headers and field hints are the same component, not two copies that
 * drift apart.
 */
import type { ReactNode } from 'react';
import { Info } from 'lucide-react';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';

/** Small info-icon tooltip used to explain each field on the order form. */
export function Hint({ text }: { text: string }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button type="button" tabIndex={-1} aria-label={text} className="inline-flex text-slate-400 hover:text-slate-600 align-middle">
          <Info className="h-3.5 w-3.5" />
        </button>
      </TooltipTrigger>
      <TooltipContent className="max-w-[260px]">{text}</TooltipContent>
    </Tooltip>
  );
}

/** A section heading with an optional required mark and an explanatory hint. */
export function SectionTitle({ children, required, hint }: { children: ReactNode; required?: boolean; hint: string }) {
  return (
    <span className="flex items-center gap-1 text-sm font-semibold text-slate-700">
      <span>{children}{required && <span className="text-red-500"> *</span>}</span>
      <Hint text={hint} />
    </span>
  );
}

/**
 * A bordered card with a titled header — the shape every section of the order
 * form's items area takes (Items, Supplies, Prescriber, …).
 */
export function OrderSection({ icon, title, required, hint, children }: {
  icon?: ReactNode;
  title: ReactNode;
  required?: boolean;
  hint: string;
  children: ReactNode;
}) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white">
      <div className="flex items-center gap-1.5 border-b border-slate-100 px-4 py-2.5">
        {icon}
        <SectionTitle required={required} hint={hint}>{title}</SectionTitle>
      </div>
      <div className="px-4 py-3">{children}</div>
    </div>
  );
}

/**
 * A sig typed over several lines in the Directions box, as one line for the
 * pharmacy. Pharmacy sig fields (NCPDP SCRIPT and the label printers behind
 * Axiom and Partell) don't take line breaks, so each break becomes a space.
 */
export function oneLineSig(text: string): string {
  return text.replace(/\s*[\r\n]+\s*/g, ' ').trim();
}
